import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { receiptBodyLines } from "../lib/retail/receiptText";
import { Loader2, ArrowLeft, Clock, CalendarClock, CheckCircle2, Printer, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import printerService from "../services/printerService";
import { customerKey, storefrontPath, loadOrderAccessToken, saveOrderAccessToken, clearVerifiedProof } from "../lib/retailSessionKeys";
import LocalDeliveryRecovery from "../components/retail/customer/LocalDeliveryRecovery";
import VerificationModal from "../components/retail/customer/VerificationModal";
import { isValidEmail, isValidPhone, formatUsPhoneDisplay } from "../lib/contactValidation";
import {
  orderPaymentBreakdown,
  externalPaidLabel,
  loyaltyRedeemedLabel,
} from "../lib/orderPaymentBreakdown";
import { fulfillmentPromise, modeLabel } from "../lib/retail/fulfillmentPresentation";
import TakeoutBadge from "../components/retail/TakeoutBadge";

// Phase 3D — Public Retail Order Status page (`/retail/:slug/orders/:code`).
// Polls every 8s so a customer waiting for their pickup gets near-real-time
// status changes without us standing up websockets yet.

const RETAIL_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/retail/public`;
// Security correction (Aug 2026) — the verified-OTP-proof recovery
// endpoint lives in the OTHER public namespace (`routes/commerce.py`'s
// `/api/public/retail/...`, the same one `PastOrdersLookup.jsx` already
// uses for its own proof-gated calls), not `/api/retail/public/...`
// above. Two genuinely different route prefixes — see
// `routes/retail_cart.py`'s own module docstring for why this mirrored
// naming exists and is easy to get backwards.
const PUBLIC_PROOF_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/retail`;

function formatMoney(amount, currency) {
  if (amount == null) return "";
  const symbol = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[(currency || "").toUpperCase()] || "";
  return symbol ? `${symbol}${Number(amount).toFixed(2)}` : `${Number(amount).toFixed(2)} ${currency || ""}`.trim();
}

const STATUS_LABEL = {
  placed: "Placed",
  // Restaurant Pilot Readiness (Mar 2026) — lifecycle stages from
  // `retail/orders_state.py` so the progress strip shows the same flow
  // a real restaurant uses: Received → Preparing → Ready → Completed.
  created: "Received",
  confirmed: "Confirmed",
  preparing: "Preparing",
  ready: "Ready",
  completed: "Completed",
  cancelled: "Cancelled",
  refunded: "Refunded",
};

const STATUS_COLOR = {
  placed: "bg-slate-100 text-slate-700",
  created: "bg-slate-100 text-slate-700",
  confirmed: "bg-blue-100 text-blue-700",
  preparing: "bg-amber-100 text-amber-800",
  ready: "bg-emerald-100 text-emerald-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
  refunded: "bg-red-100 text-red-700",
};

// Kitchen/Delivery/Payment status-axis separation correction (Aug
// 2026) — confirmed gap: this page's badge/timeline above is driven
// ENTIRELY by the restaurant's own kitchen `state`/`status`. A Local
// Delivery order whose kitchen work reached `completed` while the
// courier never showed up (or the delivery outright failed) showed the
// customer a plain green "Completed" badge with NO mention of delivery
// at all — exactly the confirmed `UBE-ORD-000023` shape (kitchen
// Completed, delivery genuinely Failed, payment eventually refunded).
// Customer-safe copy — never a raw internal code, never "paid"/
// "charged" language this page doesn't already use, never implies the
// courier is still coming once a terminal outcome is known.
const DELIVERY_LABEL = {
  quote_pending: "Preparing your delivery",
  quoted: "Preparing your delivery",
  dispatch_pending: "Requesting your courier",
  courier_requested: "Requesting your courier",
  courier_assigned: "A courier has been assigned",
  en_route_to_pickup: "Your courier is heading to the restaurant",
  at_pickup: "Your courier has arrived at the restaurant",
  picked_up: "Your order is on its way",
  en_route_to_dropoff: "Your order is on its way",
  delivered: "Delivered",
  delivery_failed: "This delivery could not be completed",
  canceled: "This delivery was canceled",
  returned: "This delivery is being returned to the restaurant",
  reconciliation_required: "We're reviewing this delivery",
};
const DELIVERY_COLOR = {
  quote_pending: "text-slate-600", quoted: "text-slate-600",
  dispatch_pending: "text-slate-600", courier_requested: "text-slate-600",
  courier_assigned: "text-sky-700", en_route_to_pickup: "text-sky-700",
  at_pickup: "text-sky-700", picked_up: "text-sky-700", en_route_to_dropoff: "text-sky-700",
  delivered: "text-emerald-700",
  delivery_failed: "text-rose-700", canceled: "text-rose-700",
  returned: "text-amber-700", reconciliation_required: "text-amber-700",
};
const DELIVERY_TERMINAL_FAILURE = new Set(["delivery_failed", "canceled"]);
const DELIVERY_IN_PROGRESS = new Set([
  "quote_pending", "quoted", "dispatch_pending", "courier_requested",
  "courier_assigned", "en_route_to_pickup", "at_pickup", "picked_up", "en_route_to_dropoff",
]);

export default function RetailOrderStatusPage() {
  const { slug, orderCode } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Security correction (Aug 2026) — `order_code` alone (a plain,
  // per-tenant sequential counter — see `retail/orders.py::
  // _format_order_code`) no longer authorizes this page's data; a
  // per-order access credential is required (see
  // `retailSessionKeys.js`). `needsAccess` covers BOTH "this order
  // doesn't exist" and "it exists but this browser has no/an invalid
  // credential" — deliberately indistinguishable, so the UI can never
  // confirm an arbitrary order_code exists.
  const [needsAccess, setNeedsAccess] = useState(false);
  // Bumped after a successful access recovery to force the poll effect
  // below to re-run immediately with the freshly-stored credential,
  // rather than waiting up to 8s for the next scheduled poll.
  const [refreshTick, setRefreshTick] = useState(0);

  // Feb 2026 — cleanly end the customer's session so the storefront
  // never reopens the just-paid cart. Rotates the sessionId + drops
  // the cached customer profile before navigating.
  //
  // PR #41 hardening — this used to remove the UNSCOPED legacy key
  // `"retail_customer_v1"`, a stale reference from before the cross-
  // tenant session-leak fix scoped it to `retail_customer_v1:{slug}`
  // (see RetailCustomer.jsx/lib/retailSessionKeys.js) — so "end the
  // session" silently did nothing. Also now returns to the SAME
  // catalog-specific storefront the customer ordered from (when one was
  // recorded this session) instead of the bare `/retail/:slug` route,
  // which re-resolves to "any published catalog" for the tenant.
  const startNewOrder = () => {
    try {
      const fresh = `r-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
      sessionStorage.setItem("retail_session_id", fresh);
      sessionStorage.removeItem(customerKey(slug));
      // Shared-kiosk correction (Aug 2026) — "Start New Order" from
      // the order-status page (the landing page for cash orders and
      // tenant-review-authorized orders) must end a verified member's
      // Favorites session exactly like it already ends the saved
      // customer record, so the next kiosk customer never restores it.
      clearVerifiedProof(slug);
    } catch (_e) { /* noop */ }
    navigate(storefrontPath(slug), { replace: true });
  };

  useEffect(() => {
    let alive = true;
    const load = () => {
      axios.get(`${RETAIL_BASE}/${slug}/orders/${orderCode}`, {
        headers: { "X-Order-Access": loadOrderAccessToken(slug, orderCode) },
      })
        .then((r) => {
          if (!alive) return;
          setOrder(r.data.order);
          setNeedsAccess(false);
          setError(null);
          setLoading(false);
        })
        .catch((e) => {
          if (!alive) return;
          const status = e?.response?.status;
          if (status === 404) {
            // Neutral by design (see `needsAccess`'s own comment above)
            // — never "Order not found", which would confirm a guessed
            // order_code is wrong rather than merely unproven.
            setNeedsAccess(true);
          } else {
            setError("Could not load order status.");
          }
          setLoading(false);
        });
    };
    load();
    const t = setInterval(load, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [slug, orderCode, refreshTick]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-6 text-center">
        <div>
          <div className="text-lg font-semibold mb-2">{error}</div>
          <Link to={storefrontPath(slug)} className="text-sm text-slate-500 underline">Back to menu</Link>
        </div>
      </div>
    );
  }
  if (needsAccess) {
    return (
      <AccessRecoveryScreen
        slug={slug}
        orderCode={orderCode}
        onRecovered={() => {
          setLoading(true);
          setNeedsAccess(false);
          setRefreshTick((n) => n + 1);
        }}
      />
    );
  }

  // Restaurant Pilot Readiness (Mar 2026) — prefer the richer `state`
  // (created→completed) over the legacy `status` (placed) so the
  // progress strip shows real kitchen progress.
  const stage = order.state || order.status || "placed";
  const isTerminal = stage === "completed" || stage === "cancelled" || stage === "refunded";
  const Icon = isTerminal ? CheckCircle2 : Clock;

  // Feb 2026 — treat orders whose payment has cleared as
  // "session-terminating". The customer should NOT be nudged back to
  // the menu on the same session; instead they get "Start New Order"
  // + "Print Receipt" as the only sanctioned next steps.
  const paymentPaid = (order.payment_status || "").toLowerCase() === "paid";
  const sessionClosed = paymentPaid || isTerminal;

  // Catalog Fulfillment Scheduling (Sep 2026) — `null` for an ASAP order,
  // so the block below simply does not render and the ASAP experience is
  // byte-for-byte what it was.
  const promise = fulfillmentPromise(order);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6" data-testid="retail-order-status-page">
      <div className="max-w-screen-sm mx-auto">
        {sessionClosed ? (
          <button
            type="button"
            onClick={startNewOrder}
            data-testid="retail-order-start-new"
            className="inline-flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700 mb-4 font-semibold"
          >
            <ArrowLeft className="w-4 h-4" /> Start New Order
          </button>
        ) : (
          <Link
            to={storefrontPath(slug)}
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Back to menu
          </Link>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Order</div>
              <div className="text-xl font-mono font-semibold mt-0.5">{order.order_code}</div>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${STATUS_COLOR[stage] || STATUS_COLOR.placed}`} data-testid="retail-status-badge">
              <Icon className="w-3.5 h-3.5" /> {STATUS_LABEL[stage] || stage}
            </span>
          </div>

          <div className="text-xs text-slate-500">
            Placed {order.placed_at ? new Date(order.placed_at).toLocaleString() : ""}
          </div>

          <StatusTimeline status={stage} recoveryEligible={order.local_delivery_recovery?.eligible} />

          {/* Catalog-level Stripe authorization/tenant-review (Aug
              2026) — for a `tenant_review` catalog, the kitchen axis
              above never advances until AFTER capture succeeds, so a
              customer whose order is merely authorized would otherwise
              see nothing but a static "Placed" badge with no
              explanation. Never says the card was charged before
              capture actually succeeds; never says it was charged at
              all once the authorization has been released. */}
          <TenantReviewPaymentNotice paymentStatus={order.payment_status} />

          {/* Kitchen/Delivery/Payment status-axis separation correction
              (Aug 2026) — a genuinely independent block, never folded
              into the kitchen timeline above, so a completed kitchen
              status can never visually hide a delivery outcome. Omitted
              entirely for a non-delivery order — never invents a
              delivery state for an order that never had one. */}
          {order.fulfillment?.mode === "delivery" && (
            <DeliveryProgress
              deliveryStatus={order.delivery_status}
              trackingUrl={order.delivery_tracking_url}
              kitchenStage={stage}
            />
          )}

          {/* Catalog Fulfillment PR 4 (Aug 2026) — customer-safe status
              for an Own Delivery or Online Customer Pickup order. Keyed
              purely on `order.fulfillment_status` (the backend's own
              provider-neutral, PII-free projection — see
              `retail/fulfillment_status_projection.py`), which is
              `undefined`/absent for every Uber/legacy/dine-in/takeout
              order, so this never overlaps with `DeliveryProgress`
              above (Uber-only) — an order can only ever have one of the
              two fields set. Never renders an internal enum name; the
              projection's own `status_text` is already customer-facing
              wording. */}
          {order.fulfillment_status && (
            <FulfillmentStatusCard status={order.fulfillment_status} />
          )}

          {/* PR #103 acceptance-review correction (Aug 2026) — this
              order-status page (reached by order_code — a bookmark, an
              email link, or the returning-customer past-orders "reopen"
              link) previously showed only "refunded — contact staff"
              even when the backend's own same-order-recovery policy
              (the SAME one RetailPaymentReturn.jsx's immediate post-
              Stripe-redirect page already offers) said recovery was
              still available. Reuses the EXISTING LocalDeliveryRecovery
              component and the server-computed eligibility from this
              route's own response — never a client-side re-derivation
              of that policy. `orderCode` doubles as the `orderId` value
              here (never the internal order id, which
              `order_to_response` deliberately never exposes on this
              public route) — every backend call this component makes
              already accepts an order_code interchangeably with the
              internal id (see `retail/orders.py::order_to_response`'s
              own test coverage for that exact contract). */}
          {order.payment_status === "refunded" && order.local_delivery_recovery?.eligible && (
            <div data-testid="retail-order-status-recovery">
              <p className="text-sm text-slate-600">
                This order's payment was refunded, but you can still get a new delivery quote
                for this same order.
              </p>
              <LocalDeliveryRecovery
                slug={slug}
                orderId={order.order_code}
                orderCode={order.order_code}
              />
            </div>
          )}

          <div className="border-t border-slate-200 pt-3 space-y-1">
            {(order.items || []).map((it) => (
              <div key={it.item_id} className="flex items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="truncate">{it.quantity} × {it.product_name}</span>
                  {it.spice_level_label && (
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-rose-50 text-rose-800 border-rose-300 shrink-0"
                      data-testid={`retail-status-spice-${it.item_id}`}
                    >
                      🌶 {it.spice_level_label}
                    </span>
                  )}
                  {/* Item-level Takeout marker (Sep 2026) — informational.
                      Absent on every order placed before the feature. */}
                  {it.item_takeout && (
                    <TakeoutBadge testId={`retail-status-item-takeout-${it.item_id}`} />
                  )}
                </span>
                <span className="shrink-0">{formatMoney(it.line_total, order.currency)}</span>
              </div>
            ))}
            <div className="pt-2 mt-2 border-t border-slate-200 flex items-center justify-between font-semibold">
              <span>Total</span><span>{formatMoney(order.total, order.currency)}</span>
            </div>
            {/* Loyalty display correction (Sep 2026) — points are a
                tender, so the total alone never says what the customer
                actually handed over. Rendered only when this order
                carries a redemption; every other order is unchanged. */}
            <OrderLoyaltyAllocation order={order} />
          </div>

          {/* Catalog Fulfillment Scheduling (Sep 2026) — the promise the
              customer chose and paid for, kept visible after payment.
              RUD-ORD-000045 showed only "Fulfillment: pickup" for a
              9:45 PM order, which tells the customer nothing about when
              to turn up. ASAP orders keep the simple line below. */}
          {promise && (
            <div
              className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2"
              data-testid="retail-status-scheduled-promise"
            >
              <div className="flex items-center gap-1.5 text-sm font-semibold text-stone-900">
                <CalendarClock className="h-4 w-4 text-orange-600" aria-hidden="true" />
                {promise.headline}
              </div>
              <div className="mt-0.5 text-sm text-stone-700">
                {promise.dateLabel} · {promise.windowLabel}
              </div>
            </div>
          )}

          <div className="text-xs text-slate-500">
            <div>
              Fulfillment: {modeLabel(order.fulfillment?.mode)}
              {promise ? " (scheduled)" : ""}
            </div>
            {order.fulfillment?.notes && <div>Notes: {order.fulfillment.notes}</div>}
            {order.fulfillment?.table_number && <div>Table: {order.fulfillment.table_number}</div>}
          </div>

          {/* Rudras-AI V1.0 Commerce Engine (Mar 2026) — print receipt. */}
          <PrintReceiptButton slug={slug} orderCode={order.order_code} />

          {sessionClosed && (
            <button
              type="button"
              onClick={startNewOrder}
              data-testid="retail-order-start-new-cta"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition active:scale-[0.98]"
            >
              Start New Order
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Kitchen/Delivery/Payment status-axis separation correction (Aug
// 2026) — the Local Delivery courier's own progress, entirely
// independent of the kitchen timeline above. Never claims delivery is
// still on its way once a terminal outcome (`delivery_failed`/
// `canceled`) is known; the tracking link is only ever shown while the
// delivery is genuinely still in progress, never after a terminal
// outcome (where it would be actively misleading).
function DeliveryProgress({ deliveryStatus, trackingUrl, kitchenStage }) {
  if (!deliveryStatus) {
    return (
      <div className="text-xs text-slate-500" data-testid="retail-delivery-progress-pending">
        We're preparing your delivery.
      </div>
    );
  }
  const label = DELIVERY_LABEL[deliveryStatus] || "Delivery status update";
  const color = DELIVERY_COLOR[deliveryStatus] || "text-slate-600";
  const isTerminalFailure = DELIVERY_TERMINAL_FAILURE.has(deliveryStatus);
  const isInProgress = DELIVERY_IN_PROGRESS.has(deliveryStatus);
  const kitchenDone = kitchenStage === "completed";

  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2" data-testid="retail-delivery-progress">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">Delivery</div>
      <div className={`text-sm font-semibold ${color}`} data-testid="retail-delivery-status-label">
        {label}
      </div>
      {isTerminalFailure && (
        <div className="mt-1 text-xs text-slate-600" data-testid="retail-delivery-failure-note">
          {/* Never asserts a refund happened — the payment section
              (surfaced elsewhere on this page via the existing refund/
              recovery copy above) is the only truthful source for
              that. Never implies the courier is still coming. */}
          We're sorry for the inconvenience. Please contact the restaurant if you have questions about this order.
        </div>
      )}
      {kitchenDone && isInProgress && (
        <div className="mt-1 text-xs text-slate-600" data-testid="retail-delivery-kitchen-done-note">
          Your order is ready and out for delivery — the restaurant's part is done, but delivery is still in progress.
        </div>
      )}
      {isInProgress && trackingUrl && (
        <a
          href={trackingUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="retail-delivery-tracking-link"
          className="mt-1.5 inline-block text-xs text-orange-600 hover:text-orange-700 underline"
        >
          Track your delivery
        </a>
      )}
    </div>
  );
}

// Catalog Fulfillment PR 4 (Aug 2026) — customer-safe status card for
// Own Delivery and Online Customer Pickup orders. Renders EXCLUSIVELY
// off the backend's own provider-neutral, PII-free `fulfillment_status`
// projection (`status_text`, an optional operator-entered `eta` hint,
// and — for Pickup only — the checkout-time-immutable store address/
// instructions snapshot). Never renders `status.phase` (the raw
// internal enum) directly, never a driver name/phone, never an "Uber"
// label for an Own Delivery order.
const FULFILLMENT_STATUS_COLOR = {
  delivered: "text-emerald-700",
  picked_up: "text-emerald-700",
  delivery_failed: "text-rose-700",
  delivery_cancelled: "text-rose-700",
  pickup_cancelled: "text-rose-700",
};

function FulfillmentStatusCard({ status }) {
  const color = FULFILLMENT_STATUS_COLOR[status.phase] || "text-slate-700";
  const isPickup = status.type === "pickup";
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2" data-testid="retail-fulfillment-status-card">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">
        {isPickup ? "Pickup" : "Delivery"}
      </div>
      <div className={`text-sm font-semibold ${color}`} data-testid="retail-fulfillment-status-text">
        {status.status_text}
      </div>
      {status.eta && (
        <div className="mt-1 text-xs text-slate-600 flex items-center gap-1" data-testid="retail-fulfillment-status-eta">
          <Clock className="w-3 h-3" /> ETA: {status.eta}
        </div>
      )}
      {!isPickup && status.delivery_instructions && (
        <div className="mt-1 text-xs text-slate-600" data-testid="retail-fulfillment-status-delivery-instructions">
          {status.delivery_instructions}
        </div>
      )}
      {isPickup && status.store_address && (
        <div className="mt-1.5 text-xs text-slate-700" data-testid="retail-fulfillment-status-store-address">
          {[status.store_address.line1, status.store_address.city, status.store_address.state, status.store_address.postal_code]
            .filter(Boolean).join(", ")}
        </div>
      )}
      {isPickup && status.pickup_instructions && (
        <div className="mt-1 text-xs text-slate-600" data-testid="retail-fulfillment-status-pickup-instructions">
          {status.pickup_instructions}
        </div>
      )}
    </div>
  );
}

// Catalog-level Stripe authorization/tenant-review (Aug 2026) — a
// genuinely separate, small notice.
//
// Auto-transition correction (Aug 2026, UBE-ORD-000029) — confirmed
// gap: this component previously rendered nothing at all once
// `payment_status` reached "paid" (or the transient post-capture
// "paid_fulfillment_pending" checkpoint), so a customer auto-
// transitioned here from the authorization page — or anyone who
// simply loaded this URL for an already-paid order — saw no explicit
// payment confirmation anywhere on the page. Reads `payment_status`
// directly (never the kitchen `stage`/`status`), so it can never be
// wrong about payment while implying success from kitchen progress,
// and never shows authorization/hold wording once capture is done.
function TenantReviewPaymentNotice({ paymentStatus }) {
  const ps = (paymentStatus || "").toLowerCase();
  if (ps === "paid" || ps === "paid_fulfillment_pending") {
    return (
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800" data-testid="retail-payment-status-paid">
        Payment received — you're all set.
      </div>
    );
  }
  if (ps === "authorized") {
    return (
      <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="retail-tenant-review-payment-notice-authorized">
        Payment authorization received — the restaurant is reviewing your order. You have not been charged yet.
      </div>
    );
  }
  if (ps === "capture_processing" || ps === "paid_fulfillment_pending" || ps === "captured_requires_tenant_review") {
    // Post-capture fulfillment-recovery correction (Aug 2026) — the
    // customer never needs (or should see) the internal distinction
    // between "still calling Stripe" and "captured, finishing up" —
    // both are calm, momentary "we're on it" states from their side.
    return (
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="retail-tenant-review-payment-notice-capturing">
        The restaurant has accepted your order. Finalizing payment now.
      </div>
    );
  }
  if (ps === "authorization_release_processing") {
    return (
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="retail-tenant-review-payment-notice-releasing">
        Releasing your card authorization.
      </div>
    );
  }
  if (ps === "authorization_released") {
    return (
      <div className="rounded-lg bg-slate-100 border border-slate-300 px-3 py-2 text-xs text-slate-700" data-testid="retail-tenant-review-payment-notice-released">
        This order was not accepted. Your card authorization has been released — you were never charged.
      </div>
    );
  }
  return null;
}

function StatusTimeline({ status, recoveryEligible }) {
  // Restaurant Pilot Readiness (Mar 2026) — restaurant lifecycle stages
  // (created → confirmed → preparing → ready → completed). Maps both
  // legacy `placed` and new `created` to the first step so older orders
  // continue rendering correctly.
  // (created → confirmed → preparing → ready → completed). Maps both
  // legacy `placed` and new `created` to the first step so older orders
  // continue rendering correctly.
  const steps = ["created", "confirmed", "preparing", "ready", "completed"];
  let normalised = status;
  if (normalised === "placed") normalised = "created";
  const idx = steps.indexOf(normalised);
  const cancelled = status === "cancelled" || status === "refunded";
  if (cancelled) {
    // PR #103 acceptance-review correction (Aug 2026) — the generic
    // "contact staff if unexpected" copy previously showed even when
    // same-order recovery was genuinely available, implying something
    // had gone wrong that needed staff intervention when a normal,
    // self-service option existed. Kept for the cancelled case and the
    // not-recovery-eligible refunded case (both genuinely may warrant a
    // "is this expected?" check); replaced with truthful, non-alarming
    // copy when recovery IS being offered right below this block.
    const message = status === "refunded"
      ? (recoveryEligible
        ? "This order's payment was refunded because delivery couldn't be arranged in time."
        : "This order was refunded. Please contact the staff if this is unexpected.")
      : "This order was cancelled. Please contact the staff if this is unexpected.";
    return (
      <div className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2" data-testid="retail-status-timeline-terminal-message">
        {message}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2" data-testid="retail-status-timeline">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className="flex-1 flex flex-col items-center gap-1">
            <div
              data-testid={`retail-status-step-${s}`}
              data-active={i <= idx ? "1" : "0"}
              className={
                "w-3 h-3 rounded-full " +
                (i <= idx ? "bg-emerald-600" : "bg-slate-200")
              }
            />
            <div className={`text-[10px] uppercase tracking-wide text-center leading-tight ${i <= idx ? "text-slate-900 font-semibold" : "text-slate-400"}`}>
              {STATUS_LABEL[s]}
            </div>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-px ${i < idx ? "bg-emerald-600" : "bg-slate-200"}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}


// Loyalty display correction (Sep 2026) — the shared payment allocation
// for a redeeming order: what the points covered, what was actually
// collected, and the (zero) balance left. Renders nothing at all for an
// order with no loyalty tender, which keeps every ordinary receipt and
// status page exactly as it was.
function OrderLoyaltyAllocation({ order }) {
  const breakdown = orderPaymentBreakdown(order);
  if (!breakdown || !breakdown.hasLoyalty) return null;
  const currency = order?.currency;
  return (
    <div className="space-y-1 text-sm" data-testid="retail-status-loyalty-allocation">
      <div className="flex items-center justify-between">
        <span>{loyaltyRedeemedLabel(breakdown.pointsRedeemed)}</span>
        <span>− {formatMoney(breakdown.loyaltyTenderCents / 100, currency)}</span>
      </div>
      <div className="flex items-center justify-between font-semibold">
        <span>{externalPaidLabel(order?.payment_method)}</span>
        <span data-testid="retail-status-external-paid">
          {formatMoney(breakdown.externalCents / 100, currency)}
        </span>
      </div>
      <div className="flex items-center justify-between text-slate-500">
        <span>Balance due</span>
        <span>{formatMoney(breakdown.balanceDueCents / 100, currency)}</span>
      </div>
    </div>
  );
}

// Rudras-AI V1.0 Commerce Engine (Mar 2026) — print-receipt control.
// Fetches the receipt envelope from the engine, converts to ESC/POS
// bytes client-side (mirrors `backend/payments/receipt.py::to_escpos_bytes`)
// and dispatches via the active printer driver (Bluetooth by default).
// Falls back to a friendly toast when the browser can't talk to a
// printer — never blocks the order flow.
function PrintReceiptButton({ slug, orderCode }) {
  const [busy, setBusy] = useState(false);

  const onPrint = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data: receipt } = await axios.get(
        `${RETAIL_BASE}/${slug}/orders/${orderCode}/receipt`,
        { headers: { "X-Order-Access": loadOrderAccessToken(slug, orderCode) } },
      );
      if (!printerService.isSupported()) {
        toast.error("This browser can't talk to a Bluetooth printer. Open in Chrome on Android or desktop.");
        return;
      }
      // Inline ESC/POS encoder — kept tight; mirrors the backend layout.
      const enc = new TextEncoder();
      const parts = [];
      const push = (x) => parts.push(typeof x === "string" ? enc.encode(x) : x);
      const ESC = 0x1b, GS = 0x1d;
      push(new Uint8Array([ESC, 0x40])); // init
      push(new Uint8Array([ESC, 0x61, 0x01])); // center
      push(new Uint8Array([ESC, 0x45, 0x01])); push(new Uint8Array([GS, 0x21, 0x11])); // bold + double
      push((receipt.restaurant.name || "") + "\n");
      push(new Uint8Array([GS, 0x21, 0x00])); push(new Uint8Array([ESC, 0x45, 0x00]));
      if (receipt.restaurant.address) push(receipt.restaurant.address + "\n");
      if (receipt.restaurant.phone) push(receipt.restaurant.phone + "\n");
      push("\n");
      push(new Uint8Array([ESC, 0x61, 0x00])); // left
      const o = receipt.order;
      push(new Uint8Array([ESC, 0x45, 0x01]));
      push(`Order  : ${o.code}\n`);
      push(new Uint8Array([ESC, 0x45, 0x00]));
      if (o.order_type) push(`Type   : ${(o.order_type || "").replace(/_/g, " ")}\n`);
      if (o.table_number) push(`Table  : ${o.table_number}\n`);
      if (o.customer_name) push(`Guest  : ${o.customer_name}\n`);
      push(`Date   : ${(receipt.issued_at || "").slice(0, 19).replace("T", " ")}\n`);
      push("\n" + "-".repeat(32) + "\n");
      const fmt = (c) => "$" + (Math.abs(c) / 100).toFixed(2);
      // Items + totals — `lib/retail/receiptText.js`, which mirrors
      // `payments/receipt.py::to_escpos_bytes`: grouped TAXABLE /
      // NON-TAXABLE with "(T)" markers and the legend when the order has
      // per-line tax records (Oct 2026), the flat list otherwise; coupon
      // and Order Discount before tax; delivery and tip after it.
      for (const ln of receiptBodyLines(receipt)) {
        if (ln.bold) push(new Uint8Array([ESC, 0x45, 0x01]));
        push(ln.text);
        if (ln.bold) push(new Uint8Array([ESC, 0x45, 0x00]));
      }
      const t = receipt.totals;
      // Loyalty display correction (Sep 2026) — this encoder is an
      // independent copy of the backend's own ESC/POS layout
      // (`payments/receipt.py::to_escpos_bytes`), and it was the copy
      // that still printed only TOTAL followed by "Paid via CASH". The
      // envelope has always carried both extra figures; they are printed
      // here now, and only when points were actually used, so a receipt
      // for an ordinary order is byte-identical to before.
      if (t.loyalty_tender_cents) {
        const pts = Number(t.loyalty_points_redeemed) || 0;
        const redeemedLabel = pts > 0
          ? `Loyalty points (${pts})`
          : "Loyalty points";
        push(redeemedLabel.padEnd(22) + ("-" + fmt(t.loyalty_tender_cents)).padStart(10) + "\n");
        push(new Uint8Array([ESC, 0x45, 0x01]));
        const paidLabel = externalPaidLabel((receipt.payment || {}).method).toUpperCase();
        push(paidLabel.padEnd(22) + fmt(t.external_payable_cents).padStart(10) + "\n");
        push(new Uint8Array([ESC, 0x45, 0x00]));
        push("BALANCE DUE".padEnd(22) + fmt(0).padStart(10) + "\n");
      }
      push("\n");
      const p = receipt.payment;
      push(`Paid via: ${(p.method || "").toUpperCase()}   Ref: ${p.reference_number || ""}\n`);
      push("\n");
      push(new Uint8Array([ESC, 0x61, 0x01]));
      push((receipt.footer || "Thank you!") + "\n");
      push("\n\n\n");
      push(new Uint8Array([GS, 0x56, 0x00])); // cut

      const totalLen = parts.reduce((n, x) => n + x.byteLength, 0);
      const buf = new Uint8Array(totalLen);
      let off = 0;
      for (const p2 of parts) { buf.set(p2, off); off += p2.byteLength; }

      await printerService.printBytes(buf);
      toast.success("Receipt printed");
    } catch (e) {
      if (e && (e.code === "unsupported" || /bluetooth/i.test(e.message || ""))) {
        toast.error("Bluetooth not available. Try Chrome on Android or desktop.");
      } else if (e && /user cancelled/i.test(e.message || "")) {
        // user dismissed the pairing dialog — quiet failure.
      } else {
        toast.error("Could not print receipt. Please check the printer.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onPrint}
      disabled={busy}
      data-testid="retail-print-receipt"
      className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 disabled:opacity-60 transition active:scale-[0.98]"
    >
      <Printer className="w-4 h-4" />
      {busy ? "Printing…" : "Print receipt"}
    </button>
  );
}

// Security correction (Aug 2026) — shown whenever this browser has no
// (or an invalid) per-order access credential for `orderCode`. NEVER
// says "order not found" or otherwise confirms/denies the order's
// existence — the exact same neutral outcome covers a genuinely
// nonexistent order, a valid order this device just has no proof for,
// and a stale/cleared credential. Recovery re-uses the SAME verified-
// OTP-proof pattern (`VerificationModal`) `PastOrdersLookup.jsx`
// already uses, then calls the new `recover-access` endpoint
// (`routes/commerce.py`) to mint a fresh credential for this device —
// never by re-deriving one from `orderCode` alone.
function AccessRecoveryScreen({ slug, orderCode, onRecovered }) {
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [catalogId, setCatalogId] = useState(null);
  const [catalogError, setCatalogError] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoverError, setRecoverError] = useState(null);
  // Local validation feedback (never a network request) — tracked
  // per-field via blur so a message doesn't flash on every keystroke
  // while the customer is still mid-typing.
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  // A throwaway session id — this recovery flow never touches a real
  // cart/checkout session, but `/otp/issue` requires one to scope its
  // idempotency/consent-event keys.
  const [sessionId] = useState(() => `recover-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`);

  useEffect(() => {
    let alive = true;
    // Known limitation (documented, not silently swept under the rug):
    // this recovers access using the tenant's DEFAULT resolved catalog.
    // A genuinely multi-catalog tenant whose order was placed against a
    // NON-default catalog could see recovery fail to match even for the
    // true owner — same neutral message either way, never a false
    // "wrong phone/email" claim. Tenant staff retain full access via
    // the existing authenticated admin order-details route regardless.
    axios.get(`${RETAIL_BASE}/${slug}/catalog`)
      // `GET /api/retail/public/{slug}/catalog` nests the catalog under
      // `catalog.id` (see `retail/assistant.py::build_public_catalog_view`'s
      // own return shape — the same shape `RetailCatalogsAdmin.jsx`'s
      // `defR?.data?.catalog?.id` already reads correctly elsewhere).
      // This previously read the non-existent top-level `r.data.id`,
      // which is always `undefined` — silently leaving `catalogId` null
      // forever and permanently disabling the Verify button below.
      .then((r) => { if (alive) setCatalogId(r.data?.catalog?.id || null); })
      .catch(() => { if (alive) setCatalogError(true); });
    return () => { alive = false; };
  }, [slug]);

  const channel = isValidPhone(phone) ? "sms" : (isValidEmail((email || "").trim()) ? "email" : null);
  const canVerify = channel === "email" || (channel === "sms" && smsConsent);
  const phoneInvalid = phoneTouched && phone.trim() && !isValidPhone(phone);
  const emailInvalid = emailTouched && email.trim() && !isValidEmail(email.trim());

  const handleVerified = async (tokens) => {
    setShowVerify(false);
    const verified = channel && tokens?.[channel];
    if (!verified?.token) return;
    const destinationValue = channel === "sms" ? phone : email.trim();
    setRecovering(true);
    setRecoverError(null);
    try {
      const { data } = await axios.post(
        `${PUBLIC_PROOF_BASE}/${slug}/orders/${orderCode}/recover-access`,
        {
          catalog_id: catalogId,
          checkout_attempt_token: verified.token,
          destination_channel: channel,
          destination_value: destinationValue,
        },
      );
      if (data?.access_token) {
        saveOrderAccessToken(slug, orderCode, data.access_token);
        onRecovered?.();
        return;
      }
      setRecoverError("We couldn't verify access to this order. Please double-check the phone or email you used.");
    } catch (_e) {
      // Neutral — never distinguishes "wrong contact" from "order not
      // found" from a genuine server error, for the same reason the
      // read routes stay neutral.
      setRecoverError("We couldn't verify access to this order. Please double-check the phone or email you used.");
    } finally {
      setRecovering(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6" data-testid="retail-order-access-recovery">
      <div className="max-w-screen-sm mx-auto bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-5 h-5 text-slate-400" />
          <h1 className="text-lg font-semibold">Verify to view this order</h1>
        </div>
        <p className="text-sm text-slate-600 mb-4" data-testid="retail-order-access-neutral-message">
          We can't confirm access to this order on this device. If it's yours, verify the phone
          or email you used to place it.
        </p>

        <label className="block mb-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Phone number</div>
          <input
            value={phone}
            onChange={(e) => { setPhone(formatUsPhoneDisplay(e.target.value)); setEmail(""); setEmailTouched(false); }}
            onBlur={() => setPhoneTouched(true)}
            placeholder="e.g. 832-555-1234"
            inputMode="tel"
            data-testid="retail-order-access-phone"
            className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
          />
          {phoneInvalid && (
            <div className="mt-1 text-xs text-rose-600" data-testid="retail-order-access-phone-invalid">
              Enter a valid phone number.
            </div>
          )}
        </label>
        <div className="text-center text-xs text-slate-400 mb-3">or</div>
        <label className="block mb-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Email</div>
          <input
            value={email}
            onChange={(e) => { setEmail(e.target.value); setPhone(""); setPhoneTouched(false); }}
            onBlur={() => setEmailTouched(true)}
            placeholder="e.g. you@example.com"
            inputMode="email"
            data-testid="retail-order-access-email"
            className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
          />
          {emailInvalid && (
            <div className="mt-1 text-xs text-rose-600" data-testid="retail-order-access-email-invalid">
              Enter a valid email address.
            </div>
          )}
        </label>

        {channel === "sms" && (
          <label className="flex items-start gap-2 text-xs text-slate-600 leading-snug cursor-pointer mb-3" data-testid="retail-order-access-sms-consent-label">
            <input
              type="checkbox"
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              data-testid="retail-order-access-sms-consent"
              className="mt-0.5 h-4 w-4 accent-orange-500 cursor-pointer shrink-0"
            />
            <span>I agree to receive a verification code via SMS.</span>
          </label>
        )}

        {recoverError && (
          <div className="mb-3 text-xs text-rose-600" data-testid="retail-order-access-error">{recoverError}</div>
        )}

        <button
          type="button"
          disabled={!channel || !canVerify || !catalogId || recovering}
          onClick={() => setShowVerify(true)}
          data-testid="retail-order-access-verify-button"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 transition"
        >
          {recovering ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
        </button>
        {catalogError && (
          <div className="mt-2 text-xs text-slate-400">We're having trouble loading this storefront right now.</div>
        )}

        <Link to={storefrontPath(slug)} className="mt-4 block text-center text-sm text-slate-500 underline">
          Back to menu
        </Link>
      </div>

      {showVerify && channel && catalogId && (
        <VerificationModal
          slug={slug}
          catalogId={catalogId}
          sessionId={sessionId}
          customer={{ phone, email, smsConsent }}
          requiredChannels={[channel]}
          orderType="lookup"
          purpose="order_lookup"
          onClose={() => setShowVerify(false)}
          onVerified={handleVerified}
        />
      )}
    </div>
  );
}
